import { useAuthStore } from '@/stores/authStore';
import { generateIdempotencyKey } from '@/hooks/useIdempotencyKey';

export interface RequestConfig {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'get' | 'post' | 'put' | 'delete' | string;
  params?: Record<string, any>;
  data?: any;
  headers?: Record<string, string>;
}

/**
 * 清理本地存储的认证 Token 字段
 */
function clearAuthTokens() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('refreshToken');
}

let isHandlingUnauthorized = false;

/**
 * 自动拦截未登录/登录已过期 (code 40001 或 http.unauthorized)
 */
function handleUnauthorized() {
  clearAuthTokens();

  if (isHandlingUnauthorized) return;
  isHandlingUnauthorized = true;

  try {
    useAuthStore.getState().logout();
  } catch {
    // 忽略可能存在的依赖初始化异常
  } finally {
    setTimeout(() => {
      isHandlingUnauthorized = false;
    }, 1000);
  }
}

/**
 * 通用网络请求函数，包含 40001 登录失效请求拦截与 idempotency_key 自动补充
 *
 * @param config 请求配置参数
 */
export async function request<T = any>(config: RequestConfig): Promise<T> {
  const { url, method = 'GET', params, data, headers = {} } = config;

  const baseURL = import.meta.env.VITE_SERVICE_BASE_URL || '';
  let requestUrl = url.startsWith('http://') || url.startsWith('https://')
    ? url
    : `${baseURL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;

  if (params && Object.keys(params).length > 0) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null) {
        query.append(key, String(val));
      }
    });
    const queryString = query.toString();
    if (queryString) {
      requestUrl += (requestUrl.includes('?') ? '&' : '?') + queryString;
    }
  }

  const reqHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers
  };

  // 获取登录凭证 access_token 并附加到请求头 Authorization
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  if (token && !reqHeaders.Authorization) {
    reqHeaders.Authorization = `Bearer ${token}`;
  }

  const options: RequestInit = {
    method: method.toUpperCase(),
    headers: reqHeaders
  };

  let payload = data;
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    // 自动检测并生成缺失的 idempotency_key
    if ('idempotency_key' in payload && (!payload.idempotency_key || typeof payload.idempotency_key !== 'string')) {
      payload = {
        ...payload,
        idempotency_key: generateIdempotencyKey()
      };
    }
  }

  if (payload && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method!)) {
    options.body = typeof payload === 'string' ? payload : JSON.stringify(payload);
  }

  let response: Response;
  try {
    response = await fetch(requestUrl, options);
  } catch (err) {
    throw new Error(err instanceof Error ? err.message : '网络请求失败');
  }

  let resData: any;
  try {
    resData = await response.json();
  } catch {
    resData = null;
  }

  // 检查是否 401 状态码或 40001 code 或 error_key === 'http.unauthorized'
  if (
    response.status === 401 ||
    resData?.code === 40001 ||
    resData?.error_key === 'http.unauthorized'
  ) {
    // 排除登出相关接口自身的 401 响应，防止死循环递归调用 logout
    if (!url.includes('/accounts/sessions/logout')) {
      handleUnauthorized();
    }
    throw new Error(resData?.message || resData?.msg || '未登录或登录已过期');
  }

  if (!response.ok) {
    const msg = resData?.message || resData?.msg || `HTTP Error ${response.status}`;
    const err = new Error(msg);
    (err as any).code = resData?.code;
    (err as any).error_key = resData?.error_key;
    throw err;
  }

  if (resData && typeof resData === 'object') {
    if (resData.code !== undefined && resData.code !== 0 && resData.code !== 200) {
      const err = new Error(resData.message || resData.msg || `操作失败 (${resData.code})`);
      (err as any).code = resData.code;
      (err as any).error_key = resData.error_key;
      throw err;
    }
    if ('data' in resData && resData.data !== undefined) {
      return resData.data as T;
    }
  }

  return resData as T;
}

